// Dossiers de rapprochement (E4) : écarts au-delà de la tolérance, reliquats expirés et contestations. Instruction
// par les parties (observations, hypothèses d'origine, justificatifs), canal de service de la messagerie, clôture
// motivée par une personne habilitée (correction, justification technique, signalement aux services compétents).
// Aucun libellé ni champ ne qualifie une fraude : le dossier constate un écart et en cherche l'origine.

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { LIBELLES_ORIGINES_RAPPROCHEMENT } from '@carburanttrack/shared/logistique';
import { api, messageErreur } from '../../api/client';
import { useSession } from '../../app/session';
import { Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { date, dateHeure } from '../../composants/format';
import { quantite } from './communs';
import { nomProduit } from './Station';
import type { DossierLigne, FicheDossier } from './types';

const LIBELLES_ETATS: Record<string, string> = { ouvert: 'Ouvert', en_instruction: 'En instruction', clos: 'Clos' };
/** Origines du dossier : codes et libellés du package partagé (`livraison`, `reliquat_expire`, `contestation`, ...). */
const libelleOrigine = (o: string) => (LIBELLES_ORIGINES_RAPPROCHEMENT as Record<string, string>)[o] ?? o.replace(/_/g, ' ');

export function PageRapprochements() {
  const [etat, setEtat] = useState<'ouverts' | 'clos'>('ouverts');
  const q = useQuery({ queryKey: ['dossiers-rapprochement', etat], queryFn: async () => (await api<DossierLigne[]>(`/api/v1/dossiers-rapprochement?etat=${etat}`)).donnees });
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Rapprochements</h1>
          <p className="attenue">Écarts entre quantité chargée et quantité reçue au-delà de la tolérance, reliquats non livrés dans le délai et contestations. Chaque partie apporte ses observations ; la clôture est motivée.</p>
        </div>
        <div className="segments" role="tablist" aria-label="Dossiers affichés">
          <button type="button" role="tab" aria-selected={etat === 'ouverts'} onClick={() => setEtat('ouverts')}>
            À instruire
          </button>
          <button type="button" role="tab" aria-selected={etat === 'clos'} onClick={() => setEtat('clos')}>
            Clos
          </button>
        </div>
      </div>
      {q.isLoading && <Squelette lignes={4} />}
      {q.error && <MessageErreur erreur={q.error} titre="Dossiers indisponibles" />}
      {q.data && q.data.length === 0 && <Vide>{etat === 'ouverts' ? 'Aucun dossier à instruire.' : 'Aucun dossier clos.'}</Vide>}
      {q.data && q.data.length > 0 && (
        <ol className="liste-exceptions">
          {q.data.map((d, i) => (
            <li key={d.id}>
              <Link className={`exception ${d.etat === 'clos' ? '' : 'ambre'}`} to={`/rapprochements/${d.id}`}>
                <span className="numero" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="texte">
                  <span className="marque-exception">
                    {libelleOrigine(d.origine)}, {(LIBELLES_ETATS[d.etat] ?? d.etat).toLowerCase()}
                  </span>
                  <b>
                    {d.ordre?.numero ?? 'Livraison indisponible'}
                    {d.produitCode ? `, ${nomProduit(d.produitCode).toLowerCase()}` : ''}
                    {d.ecartVolume ? `, écart ${quantite(d.ecartVolume, d.unite ?? 'L')}` : ''}
                    {d.ecartPourcentage ? ` (${formaterNombre(d.ecartPourcentage, 2)} %)` : ''}
                  </b>
                  <small>
                    {d.parties.map((p) => p.nom).join(', ')} ; ouvert le {date(d.ouvertLe)}
                    {d.hypotheses.length ? ` ; ${d.hypotheses.length} hypothèse${d.hypotheses.length > 1 ? 's' : ''} d’origine` : ''}
                    {d.clotureLe ? ` ; clos le ${date(d.clotureLe)}` : ''}
                  </small>
                </span>
                <Icone nom="chevron" />
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function PageDossierRapprochement() {
  const { id } = useParams();
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['dossier-rapprochement', id], queryFn: async () => (await api<FicheDossier>(`/api/v1/dossiers-rapprochement/${id}`)).donnees, enabled: Boolean(id) });
  const [texte, setTexte] = useState('');
  const [hypotheses, setHypotheses] = useState<string[]>([]);
  const [decision, setDecision] = useState('');
  const [motif, setMotif] = useState('');
  const rafraichir = () => void client.invalidateQueries({ queryKey: ['dossier-rapprochement', id] });
  const observer = useMutation({
    mutationFn: async () => api(`/api/v1/dossiers-rapprochement/${id}/observations`, { corps: { texte, hypotheses } }),
    onSuccess: () => {
      setTexte('');
      setHypotheses([]);
      rafraichir();
    },
  });
  const cloturer = useMutation({
    mutationFn: async () => api(`/api/v1/dossiers-rapprochement/${id}/cloture`, { corps: { decision, motif } }),
    onSuccess: () => {
      rafraichir();
      void client.invalidateQueries({ queryKey: ['dossiers-rapprochement'] });
    },
  });
  if (q.isLoading) return <Squelette lignes={8} />;
  if (q.error || !q.data) return <MessageErreur erreur={q.error} titre="Dossier indisponible" />;
  const d = q.data;
  const unite = d.unite ?? 'L';
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">{libelleOrigine(d.origine)}</span>
          <h1>
            Rapprochement {d.ordre ? `de ${d.ordre.numero}` : ''} <span className={`etiquette ${d.etat === 'clos' ? 'vert' : 'ambre'}`}>{LIBELLES_ETATS[d.etat] ?? d.etat}</span>
          </h1>
          <p className="attenue">{d.motifOuverture ?? 'Motif d’ouverture non renseigné'}</p>
        </div>
        <div className="actions">
          {d.ordre && (
            <Link className="bouton" to={`/livraisons/${d.ordre.id}`}>
              Fiche de la livraison
            </Link>
          )}
          {d.canalMessagerieId && (
            <Link className="bouton" to={`/messages/${d.canalMessagerieId}`}>
              Canal du dossier
            </Link>
          )}
        </div>
      </div>
      <div className="deux-colonnes">
        <div className="principal">
          <section className="carte-bloc" aria-labelledby="titre-constat">
            <h2 id="titre-constat">Constat</h2>
            <dl className="liste-definitions">
              <dt>Produit</dt>
              <dd>{d.produitCode ? nomProduit(d.produitCode) : <span className="attenue">indisponible</span>}</dd>
              <dt>Quantité chargée</dt>
              <dd>{d.quantiteChargee ? quantite(d.quantiteChargee, unite) : <span className="attenue">indisponible</span>}</dd>
              <dt>Quantité reçue</dt>
              <dd>{d.quantiteRecue ? quantite(d.quantiteRecue, unite) : <span className="attenue">indisponible</span>}</dd>
              <dt>Écart</dt>
              <dd>
                {d.ecartVolume ? quantite(d.ecartVolume, unite) : <span className="attenue">indisponible</span>}
                {d.ecartPourcentage ? ` (${formaterNombre(d.ecartPourcentage, 2)} %)` : ''}
              </dd>
              <dt>Tolérance appliquée</dt>
              <dd>{d.toleranceAppliquee ? quantite(d.toleranceAppliquee, unite) : <span className="attenue">sans objet (reliquat ou contestation)</span>}</dd>
              <dt>Parties</dt>
              <dd>{d.parties.map((p) => p.nom).join(', ')}</dd>
              <dt>Hypothèses d’origine</dt>
              <dd>{d.hypotheses.length ? d.hypotheses.map((h) => h.libelle).join(', ') : <span className="attenue">aucune retenue à ce stade</span>}</dd>
              {d.decision && (
                <>
                  <dt>Clôture</dt>
                  <dd>
                    {d.decision.libelle} le {dateHeure(d.clotureLe)} : {d.decision.motif ?? 'motif non renseigné'}
                  </dd>
                </>
              )}
            </dl>
          </section>
          {d.receptions.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-receptions-dossier">
              <h2 id="titre-receptions-dossier">Réceptions</h2>
              <ul className="liste-lignes">
                {d.receptions.map((r) => (
                  <li key={r.id}>
                    <span>
                      <b>
                        Réception {r.rang} : {quantite(r.quantite, unite)} {r.partielle ? '(partielle)' : ''}
                      </b>
                      <small>
                        {dateHeure(r.horodatage)}
                        {r.ecart ? `, écart ${quantite(r.ecart, unite)}` : ''}
                        {r.dansTolerance === false ? ', au-delà de la tolérance' : r.dansTolerance ? ', dans la tolérance' : ''}
                      </small>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="carte-bloc" aria-labelledby="titre-observations">
            <h2 id="titre-observations">Observations des parties</h2>
            {d.observations.length === 0 ? (
              <p className="attenue">Aucune observation pour l’instant.</p>
            ) : (
              <ul className="liste-lignes">
                {d.observations.map((o) => (
                  <li key={o.id}>
                    <span>
                      <b>
                        {o.organisation}, {o.auteur}
                      </b>
                      <small>
                        {dateHeure(o.le)}
                        {o.nature !== 'observation' ? `, ${o.nature.replace(/_/g, ' ')}` : ''}
                        {o.hypotheses.length ? `, hypothèses : ${o.hypotheses.map((h) => d.referentiel.hypotheses.find((x) => x.code === h)?.libelle ?? h).join(', ')}` : ''}
                      </small>
                      <span>{o.texte}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {d.droits.observer && d.etat !== 'clos' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  observer.mutate();
                }}
              >
                <div className="champ">
                  <label htmlFor="o-texte">Votre observation</label>
                  <textarea id="o-texte" value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} minLength={3} maxLength={4000} required />
                </div>
                <fieldset>
                  <legend>Hypothèses d’origine de l’écart</legend>
                  <div className="puces">
                    {d.referentiel.hypotheses.map((h) => (
                      <button key={h.code} type="button" className="puce" aria-pressed={hypotheses.includes(h.code)} onClick={() => setHypotheses(hypotheses.includes(h.code) ? hypotheses.filter((x) => x !== h.code) : [...hypotheses, h.code])}>
                        {h.libelle}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <div className="actions espace-haut">
                  <button type="submit" className="bouton primaire" disabled={observer.isPending || texte.trim().length < 3}>
                    Ajouter l’observation
                  </button>
                </div>
                {observer.error && <Message type="erreur">{messageErreur(observer.error)}</Message>}
              </form>
            )}
          </section>
        </div>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="titre-pieces">
            <h2 id="titre-pieces">Justificatifs</h2>
            {d.pieces.length === 0 ? (
              <p className="attenue">Aucun justificatif joint.</p>
            ) : (
              <ul className="liste-lignes">
                {d.pieces.map((p) => (
                  <li key={p.id}>
                    <Link to={`/documents/${p.documentId}`}>
                      <span>
                        <b>{p.libelle}</b>
                        <small>
                          {p.titre ?? 'document'}, {p.organisation}, {date(p.le)}
                        </small>
                      </span>
                      <Icone nom="chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {d.droits.observer && d.etat !== 'clos' && <PieceJointe dossierId={d.id} surAjout={rafraichir} />}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-historique-dossier">
            <h2 id="titre-historique-dossier">Historique</h2>
            <ol className="liste-lignes">
              {d.historique.map((h) => (
                <li key={h.id}>
                  <span>
                    <b>{h.type.replace(/_/g, ' ')}</b>
                    <small>
                      {dateHeure(h.le)}
                      {h.commentaire ? `, ${h.commentaire}` : ''}
                    </small>
                  </span>
                </li>
              ))}
            </ol>
          </section>
          {d.droits.cloturer && d.etat !== 'clos' && (
            <form
              className="carte-bloc"
              aria-labelledby="titre-cloture"
              onSubmit={(e) => {
                e.preventDefault();
                cloturer.mutate();
              }}
            >
              <h2 id="titre-cloture">Clôturer le dossier</h2>
              <div className="champ">
                <label htmlFor="cl-decision">Décision</label>
                <select id="cl-decision" value={decision} onChange={(e) => setDecision(e.target.value)} required>
                  <option value="">Choisir</option>
                  {d.referentiel.decisions.map((x) => (
                    <option key={x.code} value={x.code}>
                      {x.libelle}
                    </option>
                  ))}
                </select>
              </div>
              <div className="champ">
                <label htmlFor="cl-motif">Motif</label>
                <textarea id="cl-motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} minLength={3} maxLength={1000} required />
              </div>
              <button type="submit" className="bouton primaire" disabled={cloturer.isPending || !decision || motif.trim().length < 3}>
                Clôturer
              </button>
              {cloturer.error && <Message type="erreur">{messageErreur(cloturer.error)}</Message>}
            </form>
          )}
        </aside>
      </div>
    </div>
  );
}

/** Dépôt d'un justificatif : le document est d'abord créé (lot 1, chiffré), puis rattaché au dossier. */
function PieceJointe({ dossierId, surAjout }: { dossierId: string; surAjout: () => void }) {
  const { etat } = useSession();
  const organisationId = etat?.affectations?.[0]?.organisationId ?? null;
  const [fichier, setFichier] = useState<File | null>(null);
  const [libelle, setLibelle] = useState('');
  const envoi = useMutation({
    mutationFn: async () => {
      const f = new FormData();
      f.append('titre', libelle);
      f.append('categorie', 'justificatif');
      f.append('confidentialite', 'confidentielle');
      // Le justificatif est déposé dans les documents de l'organisation de la partie, puis rattaché au dossier.
      f.append('objetType', 'organisation');
      f.append('objetId', organisationId ?? '');
      f.append('fichier', fichier!);
      const doc = (await api<{ id: string }>('/api/v1/documents', { formulaire: f })).donnees;
      await api(`/api/v1/dossiers-rapprochement/${dossierId}/pieces`, { corps: { documentId: doc.id, libelle } });
    },
    onSuccess: () => {
      setFichier(null);
      setLibelle('');
      surAjout();
    },
  });
  return (
    <form
      className="espace-haut"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <div className="champ">
        <label htmlFor="pj-libelle">Libellé du justificatif</label>
        <input id="pj-libelle" value={libelle} onChange={(e) => setLibelle(e.target.value)} minLength={3} maxLength={200} required />
      </div>
      <div className="champ">
        <label htmlFor="pj-fichier">Fichier (PDF ou image)</label>
        <input id="pj-fichier" type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} required />
      </div>
      <button type="submit" className="bouton" disabled={envoi.isPending || !fichier || libelle.trim().length < 3}>
        Joindre
      </button>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
    </form>
  );
}
